import {
  BUTTON,
  CHARTS,
  CLOSE_BUTTON,
  CONTAINER,
  IMAGE,
  MINI_PLAYER,
  NUMBER_PICKER,
  POLL_OPTION,
  QUIZ_OPTION,
  SCROLL_CONTAINER,
  SLIDER,
  TEXT,
} from '../../constants.js';
import { is } from '../../is.js';
import { getStyleDeclaration } from './get-style-declaration.js';
import { buttonWidget } from './button-widget.js';
import { imageWidget } from './image-widget.js';
import { containerWidget } from './container-widget.js';
import { pollOptionWidget } from './poll-option-widget.js';
import { textWidget } from './text-widget.js';
import { closeButtonWidget } from './close-button-widget.js';
import { miniPlayerWidget } from './mini-player-widget/mini-player-widget.js';
import { transitionsRules } from './transitions-rules.js';
import { getClassDeclaration } from './get-class-declaration.js';
import { scrollContainerWidget } from './scroll-container-widget.js';
import { chartWidget } from './chart-widget.js';
import { widgetSelector } from './helpers';
import { numberPickerWidget } from './number-picker-widget.ts';
import { sliderWidget } from './slider-widget.ts';

const typeGenerators = {
  [IMAGE]: imageWidget,
  [BUTTON]: buttonWidget,
  [CONTAINER]: containerWidget,
  [TEXT]: textWidget,
  [MINI_PLAYER]: miniPlayerWidget,
  [SCROLL_CONTAINER]: scrollContainerWidget,
  [CHARTS.GAUGE]: chartWidget,
  [NUMBER_PICKER]: numberPickerWidget,
  [SLIDER]: sliderWidget,
};

const roleGenerators = {
  [CLOSE_BUTTON]: closeButtonWidget,
  [POLL_OPTION]: pollOptionWidget,
  [QUIZ_OPTION]: pollOptionWidget,
};

/**
 * Генеририет CSS declaration на основе описания виджетов.
 *
 * @param {Schema} schema
 * @return {*}
 */
export function generateStyles(schema) {
  /**
   * @example [
   * selector, [
   * 		[property, value],
   * 		[property, value],
   * 	],
   * 	optional media query
   * ]
   */
  const rules = [
    [`#${schema.id}`, [['font-size', schema.fontSize.byWidth]]],
    [
      `#${schema.id}`,
      [['font-size', schema.fontSize.byHeight]],
      `@media (min-aspect-ratio: ${schema.size[0].aspectRatio})`,
    ],
  ];
  const getBackground = (backgroundId) =>
    schema.backgrounds[backgroundId]?.style;

  schema.classes.forEach((classDescription) => {
    let styles = getClassDeclaration(classDescription, getBackground);
    rules.push(...styles);
  });

  for (const widgetId in schema.widgets) {
    const widget = schema.widgets[widgetId];
    const typeGenerator = typeGenerators[widget.type];
    const roleGenerator = roleGenerators[widget.role];

    typeGenerator && rules.push(...typeGenerator(widget));
    roleGenerator && rules.push(...roleGenerator(widget));
  }

  for (const slideId in schema.slides) {
    const slide = schema.slides[slideId];
    if (is.slideDisplayMode.standard(slide)) {
      slide.children.forEach((widget) => {
        const selector = widgetSelector(widget);
        rules.push(
          ...widget.placements.map(
            getStyleDeclaration.scaleSize(selector, 1.5, 'min-width: 1920px'),
          ),
        );
        if (widget.borderRadius) {
          rules.push([
            selector,
            [['border-radius', `${widget.borderRadius * 1.5}px`]],
            '@media screen and (min-width: 1920px)',
          ]);
        }
      });
    }
  }

  schema.style ??= [];
  schema.style.push(...rules, ...transitionsRules(schema.transitions));
  return schema;
}

/**
 * @typedef {Object} StyledState
 * @property {string} borderColor
 * @property {number} borderSize
 * @property {number} borderGap
 * @property {string} fillColor
 * @property {number} scale
 * @property {number} [borderGap] Applied for poll options and optional option's image, describe border offset.
 */

/**
 * @typedef {Object} StyledStates
 * @property {StyledState} default
 * @property {StyledState} click
 * @property {StyledState} hover
 */
